import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { LoginComponent } from '../login/login.component';
import { AuthService } from '../../services/apis/platform/auth.service';
import { UserService } from '../../services/apis/platform/user.service';
import { UtilService } from '../../services/util.service';
import { AlertService, AlertType } from '../../services/alert.service';
import { UsuarioLoginResponse } from '../../services/responses/platform/usuario-login.response';
import { cpfValidator } from '../../validators/cpf.validator';
import {
  PUBLICATION_BOOK_TYPE_OPTIONS,
  PUBLICATION_BUDGET_OPTIONS,
  PUBLICATION_TEXT_READY_OPTIONS,
  PUBLICATION_TIMEFRAME_OPTIONS,
  PublicationProfileRequest
} from '../../constants/user-publication-profile.constants';

@Component({
  selector: 'app-user-register',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDialogContent,
    MatDialogActions,
    MatProgressSpinnerModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    CommonModule,
    NgxMaskDirective
  ],
  providers: [
    provideNgxMask()
  ],
  templateUrl: './user-register.component.html',
  styleUrl: './user-register.component.css'
})
export class UserRegisterComponent implements OnInit {
  public model: FormGroup;
  public _isLoading = false;
  private ip: string = '192.168.0.2';

  public showComplementaryFields = false;
  public isEditComplementaryMode = false;

  public bookTypeOptions = PUBLICATION_BOOK_TYPE_OPTIONS;
  public textReadyOptions = PUBLICATION_TEXT_READY_OPTIONS;
  public timeframeOptions = PUBLICATION_TIMEFRAME_OPTIONS;
  public budgetOptions = PUBLICATION_BUDGET_OPTIONS;

  constructor(private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<UserRegisterComponent>,
    public dialog: MatDialog,
    public authService: AuthService,
    public userService: UserService,
    public utilService: UtilService,
    public alertService: AlertService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.getIP();
    this.showComplementaryFields = !!this.data?.cadastrarUsuarioComTodasInformacoes;
    this.isEditComplementaryMode = this.showComplementaryFields && !!this.data?.idUser;
  }

  ngOnInit(): void {
    if (this.isEditComplementaryMode) {
      // Usuário já existe: só os dados complementares, todos obrigatórios
      this.model = this.formBuilder.group({
        publicationBookType: [null, [Validators.required]],
        publicationTextReady: [null, [Validators.required]],
        publicationTimeframe: [null, [Validators.required]],
        publicationBudget: [null, [Validators.required]],
      });
      return;
    }

    this.model = this.formBuilder.group({
      name: ['', [Validators.required]],
      cpf: ['', [Validators.required, cpfValidator()]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required]],
      password: ['', [Validators.required]],
    });

    if (this.showComplementaryFields) {
      this.model.addControl('publicationBookType', this.formBuilder.control(null, [Validators.required]));
      this.model.addControl('publicationTextReady', this.formBuilder.control(null, [Validators.required]));
      this.model.addControl('publicationTimeframe', this.formBuilder.control(null, [Validators.required]));
      this.model.addControl('publicationBudget', this.formBuilder.control(null, [Validators.required]));
    }
  }

  hideSenha = signal(true);
  clickEventSenha1(event: MouseEvent): void {
    this.hideSenha.set(!this.hideSenha());
    event.stopPropagation();
  }

  register() {
    if (this.model.invalid || this._isLoading) {
      return;
    }

    if (this.isEditComplementaryMode) {
      this.savePublicationProfile();
      return;
    }

    this._isLoading = true;
    let email: string = this.model.get("email")?.value;
    let password: string = this.model.get("password")?.value;
    let phone: string = this.model.get("phone")?.value;
    let name: string = this.model.get("name")?.value;
    let cpf: string = this.model.get("cpf")?.value;

    const publicationProfile = this.showComplementaryFields ? this.getPublicationProfile() : undefined;

    this.authService.register(email, password, this.ip, phone, name, cpf, publicationProfile).subscribe({
      next: (response: UsuarioLoginResponse) => {
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          this._isLoading = false;
          return;
        }

        const isAdminFlow = !!this.data?.isAdminFlow;

        if (!isAdminFlow) {
          this.authService.updateToken(response);
        }
        this._isLoading = false;
        // true sinaliza sucesso para o fluxo do simulador (result === true)
        this.dialogRef.close(true);
      },
      error: error => {
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
        this._isLoading = false;
      },
    });
  }

  savePublicationProfile(): void {
    this._isLoading = true;
    this.userService.savePublicationProfile(this.getPublicationProfile()).subscribe({
      next: response => {
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          this._isLoading = false;
          return;
        }
        this._isLoading = false;
        this.dialogRef.close(true);
      },
      error: error => {
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
        this._isLoading = false;
      },
    });
  }

  private getPublicationProfile(): PublicationProfileRequest {
    return {
      publicationBookType: this.model.get('publicationBookType')?.value,
      publicationTextReady: this.model.get('publicationTextReady')?.value,
      publicationTimeframe: this.model.get('publicationTimeframe')?.value,
      publicationBudget: this.model.get('publicationBudget')?.value,
    };
  }

  getIP(): void {
    this.utilService.getIP().subscribe({
      next: response => {
        if (response && response.ip) {
          this.ip = response.ip;
        }
      },
    });
  }

  openModalLogin() {
    // O sentinel 'login' avisa o fluxo do simulador que a cadeia continuou no
    // LoginComponent; nos demais fluxos mantém o comportamento atual (undefined).
    this.dialogRef.close(this.showComplementaryFields ? 'login' : undefined);
    const dialogRef = this.dialog.open(LoginComponent, {
      data: {},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
      }
    });
  }
}
